import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from '@mui/material';

const C = {
  start: '#1976d2',
  condition: '#fff3e0',
  conditionBorder: '#e65100',
  param: '#e3f2fd',
  paramBorder: '#1565c0',
  lookup: '#f3e5f5',
  lookupBorder: '#7b1fa2',
  formula: '#e8eaf6',
  formulaBorder: '#283593',
  mult: '#fff8e1',
  multBorder: '#f57f17',
  result: '#e8f5e9',
  resultBorder: '#2e7d32',
  end: '#616161',
  arrow: '#666',
  text: '#333',
  white: '#fff',
};

const BOX_W = 320;
const BOX_H = 40;
const GAP = 28;
const SIDE_W = 180;
const SIDE_GAP = 60;

function shortAttr(attr) {
  if (!attr) return '?';
  const parts = attr.split('.');
  return parts[parts.length - 1];
}

function truncate(text, max) {
  return text.length > max ? text.substring(0, max - 2) + '..' : text;
}

const FormulaDiagram = ({ open, onClose, formData }) => {
  if (!open || !formData) return null;

  const centerX = 220;
  let y = 20;
  const nodes = [];

  // 1. START
  nodes.push({ type: 'start', y, label: formData.name || formData.code || 'Formula' });
  y += BOX_H + GAP;

  // 2. CONDITIONS (when does this formula apply?)
  const conds = formData.conditions || [];
  if (conds.length > 0) {
    const condText = conds.map(c => `${shortAttr(c.attr)} ${c.op} ${c.value}`).join(' AND ');
    nodes.push({ type: 'condition', y, label: condText });
    y += 60 + GAP;
  } else {
    nodes.push({ type: 'condition', y, label: 'Always applies', fallback: true });
    y += 60 + GAP;
  }

  // 3. PARAMETERS
  const params = formData.parameters || [];
  if (params.length > 0) {
    const paramLines = params.map(p => `${p.alias || '?'} = ${shortAttr(p.attr)}`);
    nodes.push({ type: 'params', y, lines: paramLines });
    y += Math.max(BOX_H, 20 + paramLines.length * 18) + GAP;
  }

  // 4. PRICE LOOKUPS
  const lookups = formData.priceLookups || [];
  if (lookups.length > 0) {
    const lookupLines = lookups.map(l => `<<${l.alias || '?'}>> = ${l.type?.toUpperCase()} lookup (${shortAttr(l.key1Attr)}${l.key2Attr ? ', ' + shortAttr(l.key2Attr) : ''})`);
    nodes.push({ type: 'lookups', y, lines: lookupLines });
    y += Math.max(BOX_H, 20 + lookupLines.length * 18) + GAP;
  }

  // 5. FORMULA
  nodes.push({ type: 'formula', y, label: formData.formula || '(no formula)' });
  y += BOX_H + GAP;

  // 6. MULTIPLICATORS
  const mults = formData.multiplicators || [];
  if (mults.length > 0) {
    nodes.push({ type: 'mults', y, items: mults });
    y += Math.max(BOX_H, 20 + mults.length * 22) + GAP;
  }

  // 7. RESULT
  const unitPrice = formData.unit_price || 0;
  const minValue = formData.min_value || 0;
  let resultLabel = `Result = formula`;
  if (unitPrice) resultLabel += ` x $${unitPrice}`;
  if (mults.length > 0) resultLabel += ` x multipliers`;
  if (minValue) resultLabel += ` (min: $${minValue})`;
  nodes.push({ type: 'result', y, label: resultLabel });
  y += BOX_H + GAP;

  // 8. END
  nodes.push({ type: 'end', y });
  y += BOX_H + 20;

  const svgWidth = centerX * 2 + 80;
  const svgHeight = y;

  function renderBox(y, w, h, fill, stroke, label, fontSize = 12, rx = 6) {
    return (
      <>
        <rect x={centerX - w / 2} y={y} width={w} height={h} rx={rx} ry={rx} fill={fill} stroke={stroke} strokeWidth={1.5} />
        <text x={centerX} y={y + h / 2 + 4} textAnchor="middle" fill={C.text} fontSize={fontSize} fontWeight="600">
          {truncate(label, 42)}
        </text>
      </>
    );
  }

  function renderMultiLineBox(y, lines, fill, stroke, title) {
    const h = Math.max(BOX_H, 24 + lines.length * 18);
    return (
      <>
        <rect x={centerX - BOX_W / 2} y={y} width={BOX_W} height={h} rx={6} ry={6} fill={fill} stroke={stroke} strokeWidth={1.5} />
        <text x={centerX - BOX_W / 2 + 10} y={y + 15} fill={stroke} fontSize="10" fontWeight="bold">{title}</text>
        {lines.map((line, i) => (
          <text key={i} x={centerX - BOX_W / 2 + 14} y={y + 30 + i * 18} fill={C.text} fontSize="11">
            {truncate(line, 45)}
          </text>
        ))}
      </>
    );
  }

  function arrow(y1, y2) {
    return (
      <line x1={centerX} y1={y1} x2={centerX} y2={y2} stroke={C.arrow} strokeWidth={2} markerEnd="url(#arrowD)" />
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Formula Diagram: {formData.name || formData.code}</DialogTitle>
      <DialogContent>
        <Box sx={{ overflow: 'auto', textAlign: 'center' }}>
          <svg width={svgWidth} height={svgHeight} style={{ minWidth: svgWidth }}>
            <defs>
              <marker id="arrowD" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
                <path d="M0,0 L8,4 L0,8 Z" fill={C.arrow} />
              </marker>
            </defs>

            {nodes.map((node, i) => {
              const nextNode = nodes[i + 1];

              if (node.type === 'start') {
                return (
                  <g key={i}>
                    <rect x={centerX - BOX_W / 2} y={node.y} width={BOX_W} height={BOX_H} rx={BOX_H / 2} ry={BOX_H / 2} fill={C.start} />
                    <text x={centerX} y={node.y + BOX_H / 2 + 5} textAnchor="middle" fill={C.white} fontSize="13" fontWeight="bold">
                      {truncate(node.label, 38)}
                    </text>
                    {nextNode && arrow(node.y + BOX_H, nextNode.y)}
                  </g>
                );
              }

              if (node.type === 'condition') {
                const dw = BOX_W / 2;
                const dh = 30;
                const cy = node.y + dh;
                const points = `${centerX},${node.y} ${centerX + dw},${cy} ${centerX},${node.y + dh * 2} ${centerX - dw},${cy}`;
                return (
                  <g key={i}>
                    <polygon
                      points={points}
                      fill={node.fallback ? '#f0fff0' : C.condition}
                      stroke={node.fallback ? '#66bb6a' : C.conditionBorder}
                      strokeWidth={2}
                    />
                    <text x={centerX} y={cy + 4} textAnchor="middle" fill={C.text} fontSize="11" fontWeight="500">
                      {truncate(node.label, 40)}
                    </text>
                    <text x={centerX - dw - 5} y={cy} textAnchor="end" fill={C.conditionBorder} fontSize="10" fontWeight="bold">
                      WHEN
                    </text>
                    {nextNode && arrow(node.y + dh * 2, nextNode.y)}
                  </g>
                );
              }

              if (node.type === 'params') {
                const h = Math.max(BOX_H, 24 + node.lines.length * 18);
                return (
                  <g key={i}>
                    {renderMultiLineBox(node.y, node.lines, C.param, C.paramBorder, 'PARAMETERS')}
                    {nextNode && arrow(node.y + h, nextNode.y)}
                  </g>
                );
              }

              if (node.type === 'lookups') {
                const h = Math.max(BOX_H, 24 + node.lines.length * 18);
                return (
                  <g key={i}>
                    {renderMultiLineBox(node.y, node.lines, C.lookup, C.lookupBorder, 'PRICE LOOKUPS')}
                    {nextNode && arrow(node.y + h, nextNode.y)}
                  </g>
                );
              }

              if (node.type === 'formula') {
                return (
                  <g key={i}>
                    {renderBox(node.y, BOX_W, BOX_H, C.formula, C.formulaBorder, node.label, 13)}
                    <text x={centerX - BOX_W / 2 - 5} y={node.y + BOX_H / 2 + 4} textAnchor="end" fill={C.formulaBorder} fontSize="10" fontWeight="bold">
                      FORMULA
                    </text>
                    {nextNode && arrow(node.y + BOX_H, nextNode.y)}
                  </g>
                );
              }

              if (node.type === 'mults') {
                const lines = node.items.map(m => {
                  const condText = (m.conditions || []).length > 0
                    ? m.conditions.map(c => `${shortAttr(c.attr)} ${c.op} ${c.value}`).join(' AND ')
                    : 'Always';
                  return `IF ${condText} => x${m.multiplier}`;
                });
                const h = Math.max(BOX_H, 24 + lines.length * 18);
                return (
                  <g key={i}>
                    {renderMultiLineBox(node.y, lines, C.mult, C.multBorder, 'MULTIPLICATORS')}
                    {nextNode && arrow(node.y + h, nextNode.y)}
                  </g>
                );
              }

              if (node.type === 'result') {
                return (
                  <g key={i}>
                    {renderBox(node.y, BOX_W, BOX_H, C.result, C.resultBorder, node.label, 12)}
                    {nextNode && arrow(node.y + BOX_H, nextNode.y)}
                  </g>
                );
              }

              if (node.type === 'end') {
                return (
                  <g key={i}>
                    <rect x={centerX - 40} y={node.y} width={80} height={32} rx={16} ry={16} fill={C.end} />
                    <text x={centerX} y={node.y + 20} textAnchor="middle" fill={C.white} fontSize="12" fontWeight="bold">END</text>
                  </g>
                );
              }

              return null;
            })}
          </svg>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FormulaDiagram;
